import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid,
  Tab, Tabs, TextField, Typography
} from '@mui/material';
import api from '../../utils/api';
import { toast } from 'react-toastify';

/**
 * Admin → Settings → Extract Data from Libraries
 * Tab 0: GSA Library · Tab 1: US Universities
 */
export default function ExtractDataLibrariesSettingsPanel() {
  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [cfg, setCfg] = useState({
    gsaUrl: '',
    universitiesUrl: '',
    gsaPullAllLetters: true,
    gsaEnrichRunning: false,
    gsaEnrichStatus: null,
    gsaPullRunning: false,
    gsaPullStatus: null,
    universitiesPullRunning: false,
    universitiesPullStatus: null,
    counts: { gsaCompanies: 0, usUniversities: 0, gsaMissingDetails: 0 },
  });
  const [lastResult, setLastResult] = useState(null);
  const [enrichDetails, setEnrichDetails] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/extracted-data/config');
      setCfg(r.data?.data || cfg);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load library settings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/extracted-data/config', {
        gsaUrl: cfg.gsaUrl,
        universitiesUrl: cfg.universitiesUrl,
        gsaPullAllLetters: cfg.gsaPullAllLetters,
      });
      setCfg(r.data?.data || cfg);
      toast.success('Library settings saved');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const pullGsa = async () => {
    setPulling(true);
    setLastResult(null);
    try {
      await api.put('/extracted-data/config', {
        gsaUrl: cfg.gsaUrl,
        gsaPullAllLetters: cfg.gsaPullAllLetters,
      });
      const r = await api.post('/extracted-data/gsa/pull', {
        url: cfg.gsaUrl,
        pullAllLetters: cfg.gsaPullAllLetters,
        enrichDetails,
        enrichLimit: 300,
      });
      setLastResult(r.data);
      toast.success(r.data?.message || 'GSA pull complete');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'GSA pull failed');
    } finally {
      setPulling(false);
    }
  };

  const enrichGsa = async () => {
    setPulling(true);
    try {
      const r = await api.post('/extracted-data/gsa/enrich', {
        limit: 500,
        concurrency: 2,
        onlyMissing: true,
        force: true,
      });
      toast.success(r.data?.message || 'Enrichment started');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Enrichment failed to start');
    } finally {
      setPulling(false);
    }
  };

  const pullUniversities = async () => {
    setPulling(true);
    setLastResult(null);
    try {
      await api.put('/extracted-data/config', { universitiesUrl: cfg.universitiesUrl });
      const r = await api.post('/extracted-data/universities/pull', {
        url: cfg.universitiesUrl,
      });
      setLastResult(r.data);
      toast.success(r.data?.message || 'Universities pull complete');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Universities pull failed');
    } finally {
      setPulling(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
        <Tab label="GSA Library" />
        <Tab label="US Universities" />
      </Tabs>

      {subTab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            The list page for one letter (e.g. <code>?contractorListFor=A</code>) only has ~1.8k names.
            Check <strong>Pull all letters A–Z</strong> to load the full catalog (~12k+).
            Email / phone / address / website are on each company&apos;s detail page — use
            <strong> Enrich contact details</strong> after the list pull (runs in the background; repeat until missing count is low).
          </Alert>
          <TextField
            fullWidth
            size="small"
            label="GSA contractor list URL"
            value={cfg.gsaUrl || ''}
            onChange={(e) => setCfg((c) => ({ ...c, gsaUrl: e.target.value }))}
            sx={{ mb: 1 }}
            helperText="Example: …/contractorList.do?contractorListFor=A (letter is replaced when pulling A–Z)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(cfg.gsaPullAllLetters)}
                onChange={(e) => setCfg((c) => ({ ...c, gsaPullAllLetters: e.target.checked }))}
              />
            }
            label="Pull all letters A–Z (full catalog)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={enrichDetails}
                onChange={(e) => setEnrichDetails(e.target.checked)}
              />
            }
            label="After list pull, start enriching contact details (first 300 missing)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Stored GSA companies: {(cfg.counts?.gsaCompanies || 0).toLocaleString()}
            {' · '}
            Missing email/phone/website/address: {(cfg.counts?.gsaMissingDetails || 0).toLocaleString()}
          </Typography>
          {cfg.gsaPullStatus?.status && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Catalog pull: {cfg.gsaPullStatus.status}
              {cfg.gsaPullStatus.progress ? ` · ${cfg.gsaPullStatus.progress}` : ''}
              {cfg.gsaPullStatus.created != null
                ? ` · ${cfg.gsaPullStatus.created} new / ${cfg.gsaPullStatus.updated || 0} updated`
                : ''}
              {cfg.gsaPullRunning ? ' (running…)' : ''}
            </Typography>
          )}
          {cfg.gsaEnrichStatus?.status && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Enrichment status: {cfg.gsaEnrichStatus.status}
              {cfg.gsaEnrichStatus.enriched != null ? ` · enriched ${cfg.gsaEnrichStatus.enriched}` : ''}
              {cfg.gsaEnrichStatus.attempted != null ? ` / attempted ${cfg.gsaEnrichStatus.attempted}` : ''}
              {cfg.gsaEnrichRunning ? ' (running…)' : ''}
            </Typography>
          )}
          <Grid container spacing={1}>
            <Grid item>
              <Button variant="outlined" onClick={save} disabled={saving || pulling}>
                {saving ? 'Saving…' : 'Save URL'}
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={pullGsa} disabled={pulling || !cfg.gsaUrl}>
                {pulling ? 'Working…' : 'Pull Data'}
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" color="secondary" onClick={enrichGsa} disabled={pulling}>
                Enrich contact details (next 500)
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {subTab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            The old Hipolabs API only has ~2,300 US schools. Default source is now the
            <strong> US Dept of Education College Scorecard</strong> ZIP (~6,200+ institutions)
            with city, state, ZIP, street address, and website. Pull runs in the background.
            Results appear under <strong>Extracted Data → US Universities</strong>.
          </Alert>
          <TextField
            fullWidth
            size="small"
            label="US Universities URL"
            value={cfg.universitiesUrl || ''}
            onChange={(e) => setCfg((c) => ({ ...c, universitiesUrl: e.target.value }))}
            sx={{ mb: 1 }}
            helperText="College Scorecard ZIP (recommended), Scorecard API URL, or Hipolabs JSON"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Stored universities: {(cfg.counts?.usUniversities || 0).toLocaleString()}
          </Typography>
          {cfg.universitiesPullStatus?.status && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Pull status: {cfg.universitiesPullStatus.status}
              {cfg.universitiesPullStatus.progress ? ` · ${cfg.universitiesPullStatus.progress}` : ''}
              {cfg.universitiesPullStatus.created != null
                ? ` · ${cfg.universitiesPullStatus.created} new / ${cfg.universitiesPullStatus.updated || 0} updated (parsed ${cfg.universitiesPullStatus.parsed || 0})`
                : ''}
              {cfg.universitiesPullRunning ? ' (running…)' : ''}
            </Typography>
          )}
          <Grid container spacing={1}>
            <Grid item>
              <Button variant="outlined" onClick={save} disabled={saving || pulling}>
                {saving ? 'Saving…' : 'Save URL'}
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={pullUniversities} disabled={pulling || !cfg.universitiesUrl}>
                {pulling ? 'Starting…' : 'Pull Data'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="text"
                disabled={pulling}
                onClick={() =>
                  setCfg((c) => ({
                    ...c,
                    universitiesUrl:
                      'https://ed-public-download.scorecard.network/downloads/Most-Recent-Cohorts-Institution_06102026.zip',
                  }))
                }
              >
                Use College Scorecard (full catalog)
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {lastResult && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {lastResult.message}
          {lastResult.data && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Parsed {lastResult.data.parsed} · New {lastResult.data.created} · Updated {lastResult.data.updated}
              {lastResult.data.errors?.length ? ` · Errors ${lastResult.data.errors.length}` : ''}
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
}
